import { Add, QuestionAnswer } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Alert, Avatar, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { setSearchValue, setSelectedItem } from 'app/actions/issue';
import { IconButton, InputSearch, Scrollbar } from 'app/components';
import { IssueDialog, IssueList } from 'app/pages/Issue';
import { useAsyncAction } from 'app/services';
import { issueLoad, issueStatusUpdate } from 'app/store/issue';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const Issue = () => {
  const dispatch = useDispatch();
  const { items, selectedItem, searchValue } = useSelector((state) => state.issue);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { asyncAction: fetchData, isLoading: isFetchLoading } = useAsyncAction(issueLoad);
  const {
    asyncAction: updateSubmit,
    isLoading: isUpdateLoading,
    isSuccessful: isUpdateSuccessful,
    isError: isUpdateError,
  } = useAsyncAction(issueStatusUpdate, [selectedItem]);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleUpdateClick = () => {
    updateSubmit(selectedItem.id, { okdesk_id: selectedItem.okdesk_id });
  };

  const handleIssueSearchChange = (value) => dispatch(setSearchValue(value));

  useEffect(() => {
    fetchData();

    return () => dispatch(setSelectedItem(null));
  }, []);

  return (
    <React.Fragment>
      <Stack
        direction='column'
        sx={(theme) => ({
          width: '500px',
          height: '100%',
          zIndex: '1010',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: theme.palette.divider,
        })}>
        {!isFetchLoading && (
          <React.Fragment>
            <Stack
              direction='row'
              alignItems='center'
              sx={(theme) => ({
                padding: theme.spacing(2),
              })}>
              <InputSearch placeholder='Тема заявки' value={searchValue} onChange={handleIssueSearchChange} />
              <IconButton onClick={handleDialogOpen} endIcon={<Add />} />
              <IssueDialog open={dialogOpen} onClose={handleDialogClose} />
            </Stack>
            {items.length !== 0 && (
              <Stack
                direction='column'
                alignItems='center'
                justifyContent='center'
                sx={{
                  flex: '1 1 auto',
                  overflow: 'hidden',
                }}>
                <IssueList />
              </Stack>
            )}
          </React.Fragment>
        )}
        {!isFetchLoading && items.length === 0 && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={{
              width: '100%',
              height: '100%',
            }}>
            <Typography align='center' variant='body1' component='div'>
              Здесь пока ничего нет
            </Typography>
            <Typography align='center' color='text.secondary' variant='caption' component='div'>
              В этом списке будут отображаться ваши заявки
            </Typography>
          </Stack>
        )}
        {isFetchLoading && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={{
              width: '100%',
              height: '100%',
            }}>
            <CircularProgress size={24} />
          </Stack>
        )}
      </Stack>
      <Stack
        direction='column'
        sx={{
          height: '100%',
          flex: '1 1 auto',
        }}>
        {selectedItem && (
          <Scrollbar>
            <Stack
              direction='column'
              alignItems='center'
              sx={(theme) => ({
                maxWidth: '540px',
                margin: 'auto',
                width: '100%',
                padding: theme.spacing(4, 2),
              })}>
              <Stack
                direction='column'
                alignItems='center'
                sx={(theme) => ({
                  marginBottom: theme.spacing(3),
                  '.MuiAvatar-root': {
                    width: '56px',
                    height: '56px',
                    bgcolor: theme.palette.primary.main,
                  },
                  '.MuiAvatar-circular': {
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                  },
                  '.MuiTypography-root:first-of-type': {
                    marginTop: theme.spacing(1),
                  },
                })}>
                <Avatar>
                  <QuestionAnswer
                    sx={{
                      color: selectedItem.status_color,
                    }}
                  />
                </Avatar>
                <Typography variant='body1'>{selectedItem.title}</Typography>
              </Stack>
              <Stack
                direction='column'
                alignItems='left'
                sx={(theme) => ({
                  width: '100%',
                  position: 'relative',
                  padding: theme.spacing(2),
                  border: '1px solid',
                  borderRadius: theme.shape.borderRadius,
                  borderColor: theme.palette.divider,
                  bgcolor: 'background.paper',
                  form: {
                    width: '100%',
                  },
                })}>
                <Stack
                  direction='column'
                  sx={(theme) => ({
                    width: '100%',
                    paddingBottom: theme.spacing(1),
                    '.MuiTypography-h6': {
                      fontSize: '1rem',
                    },
                    '.MuiTypography-caption': {
                      color: theme.palette.text.secondary,
                    },
                  })}>
                  <Typography variant='h6'>Информация</Typography>
                  <Typography variant='caption'>Основная информация о заявке и ее статус</Typography>
                </Stack>
                <Stack
                  direction='row'
                  sx={(theme) => ({
                    display: 'grid',
                    gap: 2,
                    '.MuiPaper-root': {
                      position: 'relative',
                      border: 'none',
                    },
                    '.MuiPaper-row': {
                      display: 'grid',
                      gap: 1,
                      marginTop: '5px',
                      fontSize: '13px',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      '.MuiTypography-root:first-of-type': {
                        color: 'text.secondary',
                      },
                    },
                    '.MuiPaper-row p.MuiTypography-root:nth-of-type(1)': {
                      gridRow: '1',
                      gridColumn: '1/3',
                    },
                    '.MuiPaper-row p.MuiTypography-root:nth-of-type(2), .MuiPaper-row div': {
                      gridRow: '1',
                      gridColumn: '3/8',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      'span:nth-of-type(2)': {
                        paddingLeft: theme.spacing(0.5),
                      },
                    },
                  })}>
                  <Paper
                    variant='outlined'
                    sx={(theme) => ({
                      marginTop: theme.spacing(1.5),
                    })}>
                    <Stack className='MuiPaper-row'>
                      <Typography variant='body1'>Создана</Typography>
                      <Typography variant='body1'>{moment(selectedItem.created_at).format('YY.MM.DD HH:mm')}</Typography>
                    </Stack>
                    <Stack className='MuiPaper-row'>
                      <Typography variant='body1'>Тема</Typography>
                      <Typography variant='body1'>{selectedItem.title}</Typography>
                    </Stack>
                    <Stack className='MuiPaper-row'>
                      <Typography variant='body1'>Номер ТС</Typography>
                      <Typography variant='body1'>{selectedItem.transport_number}</Typography>
                    </Stack>
                    <Stack className='MuiPaper-row'>
                      <Typography variant='body1'>Описание</Typography>
                      <Typography variant='body1'>{selectedItem.description}</Typography>
                    </Stack>
                    <Stack className='MuiPaper-row'>
                      <Typography variant='body1'>Статус</Typography>
                      <Typography variant='body1'>{selectedItem.status}</Typography>
                    </Stack>
                    <Stack className='MuiPaper-row'>
                      <Typography variant='body1'>Комментарии</Typography>
                      {selectedItem.comments.length !== 0 && (
                        <Typography
                          component='div'
                          sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            '.MuiTypography-root': {
                              color: '#000000 !important',
                            },
                          }}>
                          {selectedItem.comments.map((comment, key) => (
                            <Typography key={key} variant='body1'>
                              {comment}
                            </Typography>
                          ))}
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                </Stack>
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    '.MuiAlert-root': {
                      width: '100%',
                      marginTop: theme.spacing(2),
                      borderRadius: theme.shape.borderRadius,
                    },
                  })}>
                  {!isUpdateLoading && isUpdateError && (
                    <Alert severity='error'>
                      Произошла ошибка при обновлении статуса. Попробуйте снова или обратитесь в техническую поддержку.
                    </Alert>
                  )}
                  {!isUpdateLoading && isUpdateSuccessful && <Alert severity='success'>Статус обновлен.</Alert>}
                </Stack>
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    paddingTop: theme.spacing(3),
                  })}>
                  {selectedItem.status_code !== 'closed' && (
                    <LoadingButton variant='contained' loading={isUpdateLoading} onClick={handleUpdateClick} disableElevation>
                      Обновить статус
                    </LoadingButton>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Scrollbar>
        )}
        {!selectedItem && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={(theme) => ({
              height: '100%',
              padding: theme.spacing(0, 2),
              '.MuiTypography-root': {
                borderRadius: '20px',
                marginTop: '24px',
                padding: theme.spacing(0.5, 2),
                bgcolor: alpha(theme.palette.grey.main, 0.12),
              },
            })}>
            <Typography align='center' color='text.secondary' variant='caption' component='label'>
              Выберите заявку из списка
            </Typography>
          </Stack>
        )}
      </Stack>
    </React.Fragment>
  );
};

export default Issue;
